import { Card, Icon, Modal, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  CustomerApiRequestLog,
  CustomerApiRequestLogsPage,
} from '../../api/customerApiKeysApi';
import { ApiRequest, useApiResource } from '../../hooks/useApi';
import { DateFormat, useI18n } from '../../hooks/useI18n';
import { messages } from '../../messages';
import { ApiErrorDetails } from '../apiErrorDetails/apiErrorDetails';
import { DescriptionList } from '../descriptionList/descriptionList';
import { Columns, Table } from '../table';
import styles from './customerApiRequestLogs.module.scss';

const pageSize = 25;

export function CustomerApiRequestLogs({
  apiKeyId,
  getLogs,
  onGoBack,
}: {
  apiKeyId: number;
  getLogs: (
    page: number,
    pageSize: number,
  ) => ApiRequest<CustomerApiRequestLogsPage>;
  onGoBack: () => void;
}) {
  const { t, formatDate } = useI18n();
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<CustomerApiRequestLog>();
  const logs = useApiResource(getLogs, {
    emptyValue: { result: [], offset: 0, total: 0 },
    clearDataOnLoad: false,
  });

  useEffect(() => {
    logs.refresh(page, pageSize);
  }, [apiKeyId, page]);

  const columns: Columns<CustomerApiRequestLog> = [
    {
      header: t(messages.customer.apiLogTime),
      accessor: 'createdAt',
      width: 175,
      cell: ({ value }) => formatDate(value, DateFormat.DATETIME),
    },
    {
      header: t(messages.customer.apiLogMethod),
      accessor: 'method',
      width: 85,
    },
    {
      header: t(messages.customer.apiLogRequest),
      accessor: 'path',
      cell: ({ row }) => `${row.path}${row.queryString ?? ''}`,
    },
    {
      header: t(messages.customer.apiLogStatus),
      accessor: 'statusCode',
      width: 90,
      cell: ({ value }) => (
        <Tag color={value >= 400 ? 'red' : value >= 300 ? 'orange' : 'green'}>
          {value}
        </Tag>
      ),
    },
    {
      header: t(messages.customer.apiLogDuration),
      accessor: 'durationMs',
      width: 110,
      cell: ({ value }) => `${value} ms`,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.navigation} onClick={onGoBack}>
        <Icon type="left" /> {t(messages.customer.customerApi)}
      </div>
      {logs.error && <ApiErrorDetails error={logs.error} />}
      <Card title={t(messages.customer.apiLogsTitle, { apiKeyId })}>
        <Table<CustomerApiRequestLog>
          columns={columns}
          items={logs.data.result}
          isLoading={logs.isLoading}
          onSelectItem={setSelectedLog}
          paged={{
            page,
            pageSize,
            totalItems: logs.data.total,
            onPageChange: setPage,
          }}
        />
      </Card>
      <Modal
        title={t(messages.customer.apiLogDetails)}
        visible={selectedLog !== undefined}
        width={900}
        footer={null}
        onCancel={() => setSelectedLog(undefined)}
      >
        {selectedLog && (
          <>
            <DescriptionList>
              <DescriptionList.Item title={t(messages.customer.apiLogTime)}>
                {formatDate(selectedLog.createdAt, DateFormat.DATETIME)}
              </DescriptionList.Item>
              <DescriptionList.Item title={t(messages.customer.apiLogStatus)}>
                {selectedLog.statusCode}
              </DescriptionList.Item>
              <DescriptionList.Item title={t(messages.customer.apiLogDuration)}>
                {selectedLog.durationMs} ms
              </DescriptionList.Item>
              <DescriptionList.Item title={t(messages.customer.apiLogTraceId)}>
                {selectedLog.traceIdentifier}
              </DescriptionList.Item>
              <DescriptionList.Item title={t(messages.customer.apiLogRequest)}>
                {selectedLog.method} {selectedLog.path}
                {selectedLog.queryString}
              </DescriptionList.Item>
              <DescriptionList.Item
                title={t(messages.customer.apiLogRouteValues)}
              >
                {selectedLog.routeValues ?? '-'}
              </DescriptionList.Item>
            </DescriptionList>
            <LogBody
              title={t(messages.customer.apiLogRequestBody)}
              contentType={selectedLog.requestContentType}
              body={selectedLog.requestBody}
              truncated={selectedLog.requestBodyTruncated}
            />
            <LogBody
              title={t(messages.customer.apiLogResponseBody)}
              contentType={selectedLog.responseContentType}
              body={selectedLog.responseBody}
              truncated={selectedLog.responseBodyTruncated}
            />
          </>
        )}
      </Modal>
    </div>
  );
}

function LogBody({
  title,
  contentType,
  body,
  truncated,
}: {
  title: string;
  contentType?: string;
  body?: string;
  truncated: boolean;
}) {
  const { t } = useI18n();

  return (
    <div className={styles.section}>
      <span className={styles.label}>
        {title} ({contentType ?? t(messages.shared.unknown)})
        {truncated ? ` — ${t(messages.customer.apiLogTruncated)}` : ''}
      </span>
      <pre className={styles.body}>{body ?? '-'}</pre>
    </div>
  );
}
